import React, { useState } from 'react'
import { TabGroup, TabList, Tab, Flex } from '@tremor/react'
import { Calendar, CalendarDays, Phone, TrendingUp, CheckCircle2, PhoneMissed } from '@/components/icons'

import { BonusProgress } from './components/BonusProgress'
import { StatusCard } from './components/StatusCard'
import { Leaderboard } from './components/Leaderboard'
import { ActivityFeed } from './components/ActivityFeed'
import { PerformanceTable } from './components/PerformanceTable'
import { TeamFocus } from './components/TeamFocus'
import { DashboardSkeleton } from './components/DashboardSkeleton'
import type { TeamPerformanceData, Period } from './types'
import { CONVERSION_THRESHOLDS, QUALITY_THRESHOLDS } from './types'
import type { StatusLevel } from '@/styles/design-tokens'

interface TeamPerformanceDashboardProps {
  initialData: TeamPerformanceData
}

function getConversionStatus(rate: number): { level: StatusLevel; label: string } {
  if (rate >= CONVERSION_THRESHOLDS.ELITE) return { level: 'elite', label: 'Elite' }
  if (rate >= CONVERSION_THRESHOLDS.STRONG) return { level: 'strong', label: 'Strong' }
  if (rate >= CONVERSION_THRESHOLDS.APPROACHING) return { level: 'approaching', label: 'Approaching' }
  return { level: 'neutral', label: 'Building' }
}

function getQualityStatus(score: number): { level: StatusLevel; label: string } {
  if (score >= QUALITY_THRESHOLDS.EXCELLENT) return { level: 'elite', label: 'Excellent' }
  if (score >= QUALITY_THRESHOLDS.STRONG) return { level: 'strong', label: 'Strong' }
  if (score >= QUALITY_THRESHOLDS.ACCEPTABLE) return { level: 'approaching', label: 'Acceptable' }
  return { level: 'danger', label: 'Needs Work' }
}

function getMissedCallsStatus(count: number, target: number = 15): { level: StatusLevel; label: string } {
  if (count >= target) return { level: 'danger', label: 'Over Target!' }
  if (count >= target * 0.8) return { level: 'approaching', label: 'Near Limit' }
  return { level: 'strong', label: 'On Track' }
}

export function TeamPerformanceDashboard({ initialData }: TeamPerformanceDashboardProps) {
  const [period, setPeriod] = useState<Period>(initialData.period || 'month')
  const [data] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)

  const handlePeriodChange = (index: number) => {
    const newPeriod = index === 0 ? 'today' : 'month'
    setIsLoading(true)
    window.location.href = `/dashboard/team_performance?period=${newPeriod}`
  }

  const metrics = data.teamMetrics
  const isToday = period === 'today'

  const conversionStatus = getConversionStatus(metrics.conversionRate)
  const qualityStatus = getQualityStatus(metrics.qualityScoreMonth)
  const missedStatus = getMissedCallsStatus(metrics.missedCallsMonth)

  if (isLoading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="space-y-8">
      {/* Period Toggle - Industrial Tab Design */}
      <div className="flex items-center justify-between">
        <TabGroup
          index={period === 'today' ? 0 : 1}
          onIndexChange={handlePeriodChange}
        >
          <TabList variant="solid" className="bg-zinc-100 p-1 rounded-lg">
            <Tab className="px-4 py-2 text-sm font-medium rounded-md transition-all data-[selected]:bg-white data-[selected]:shadow-sm">
              <Flex alignItems="center" className="gap-2">
                <Calendar className="w-4 h-4" />
                Today
              </Flex>
            </Tab>
            <Tab className="px-4 py-2 text-sm font-medium rounded-md transition-all data-[selected]:bg-white data-[selected]:shadow-sm">
              <Flex alignItems="center" className="gap-2">
                <CalendarDays className="w-4 h-4" />
                This Month
              </Flex>
            </Tab>
          </TabList>
        </TabGroup>
      </div>

      {/* Hero: Bonus Progress */}
      <BonusProgress bonus={data.bonusProgress} />

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Calls */}
        <StatusCard
          title="Total Calls"
          value={isToday ? metrics.totalCallsToday : metrics.totalCallsMonth}
          subtitle={isToday ? 'Today' : 'This Month'}
          secondary={!isToday ? `Today: ${metrics.totalCallsToday}` : undefined}
          status="neutral"
          icon={<Phone className="w-6 h-6" />}
        />

        {/* Conversion */}
        <StatusCard
          title="Team Conversion"
          value={metrics.conversionRate.toFixed(1)}
          unit="%"
          subtitle={`Today: ${metrics.conversionRateToday.toFixed(1)}%`}
          secondary="Target: 55%"
          status={conversionStatus.level}
          statusLabel={conversionStatus.label}
          progress={{ value: metrics.conversionRate, max: 55 }}
          icon={<TrendingUp className="w-6 h-6" />}
        />

        {/* Quality */}
        <StatusCard
          title="Quality Score"
          value={metrics.qualityScoreMonth}
          subtitle={`Today: ${metrics.qualityScoreToday}`}
          secondary="Target: 80+"
          status={qualityStatus.level}
          statusLabel={qualityStatus.label}
          progress={{ value: metrics.qualityScoreMonth, max: 100 }}
          icon={<CheckCircle2 className="w-6 h-6" />}
        />

        {/* Missed Calls */}
        <StatusCard
          title="Missed Calls"
          value={metrics.missedCallsMonth}
          subtitle={`Today: ${metrics.missedCallsToday}`}
          secondary="Target: <15/month"
          status={missedStatus.level}
          statusLabel={missedStatus.label}
          progress={{ value: metrics.missedCallsMonth, max: 15 }}
          icon={<PhoneMissed className="w-6 h-6" />}
          inverted
        />
      </div>

      {/* Leaderboards and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Conversion */}
        <Leaderboard
          title="Top Conversion"
          items={data.topConversion}
          metricSuffix="%"
          icon={<TrendingUp className="w-5 h-5" />}
          accentColor="emerald"
        />

        {/* Top Quality */}
        <Leaderboard
          title="Top Quality"
          items={data.topQuality}
          metricSuffix=""
          icon={<CheckCircle2 className="w-5 h-5" />}
          accentColor="blue"
        />

        {/* Activity Feed */}
        <ActivityFeed activities={data.recentActivities} />
      </div>

      {/* Performance Table */}
      <PerformanceTable operators={data.operators} />

      {/* Team Focus */}
      <TeamFocus />
    </div>
  )
}
